import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SeasonProvider } from './context/SeasonContext';
import { ConfirmProvider } from './context/ConfirmContext';
import ModernSidebar from './components/ModernSidebar';
import DevelopmentBanner from './components/DevelopmentBanner';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import './styles/ModernSidebar.css';
import MobileBottomNav from './components/shared/MobileBottomNav';
import MobileTopBar from './components/shared/MobileTopBar';

// Route-level code splitting — each page is fetched only when first visited,
// keeping the initial bundle small.
const WarehousePage = lazy(() => import('./pages/WarehousePage'));
const FieldPage = lazy(() => import('./pages/FieldPage'));
const FieldSeasonPage = lazy(() => import('./pages/FieldSeasonPage'));
const FieldTreatmentPage = lazy(() => import('./pages/FieldTreatmentPage'));
const TrailerPage = lazy(() => import('./pages/TrailerPage'));
const DeliveryPage = lazy(() => import('./pages/DeliveryPage'));
const StatisticsPage = lazy(() => import('./pages/StatisticsPage'));
const StocksPage = lazy(() => import('./pages/StocksPage'));
const SalePage = lazy(() => import('./pages/SalePage'));
const ReportPage = lazy(() => import('./pages/ReportPage'));
const FertilizerStockPage = lazy(() => import('./pages/FertilizerStockPage'));
const SprayingProductPage = lazy(() => import('./pages/SprayingProductPage'));
const SprayingRegisterPage = lazy(() => import('./pages/SprayingRegisterPage'));
const FertilizationPlannerPage = lazy(() => import('./pages/FertilizationPlannerPage'));
const FertilizationComparisonPage = lazy(() => import('./pages/FertilizationComparisonPage'));
const FertilizerBasePage = lazy(() => import('./pages/FertilizerBasePage'));
const SprayingBasePage = lazy(() => import('./pages/SprayingBasePage'));
const SoilAnalysisPage = lazy(() => import('./pages/SoilAnalysisPage'));
const HarvestQualityPage = lazy(() => import('./pages/HarvestQualityPage'));
const NutrientBalancePage = lazy(() => import('./pages/NutrientBalancePage'));
const SeasonPricePage = lazy(() => import('./pages/SeasonPricePage'));
const ContractorPage = lazy(() => import('./pages/ContractorPage'));
const MachinesPage = lazy(() => import('./pages/MachinesPage'));
const MachineDetailPage = lazy(() => import('./pages/MachineDetailPage'));
const PartsPage = lazy(() => import('./pages/PartsPage'));
const AgOpenGpsPage = lazy(() => import('./pages/AgOpenGpsPage'));
const PasskeysPage = lazy(() => import('./pages/PasskeysPage'));

const RouteFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
    <div className="spinner-border text-success" role="status">
      <span className="visually-hidden">Ładowanie...</span>
    </div>
  </div>
);

function AppLayout() {
  return (
    <SeasonProvider>
      <div className="App app-shell">
        <ModernSidebar />

        <MobileTopBar />

        <div className="main-content">
          <DevelopmentBanner />
          <Suspense fallback={ <RouteFallback /> }>
            <Routes>
              <Route path="/" element={ <StocksPage /> } />
              <Route path="/warehouses" element={ <WarehousePage /> } />
              <Route path="/fields" element={ <FieldPage /> } />
              <Route path="/field-seasons" element={ <FieldSeasonPage /> } />
              <Route path="/field-treatments" element={ <FieldTreatmentPage /> } />
              <Route path="/trailers" element={ <TrailerPage /> } />
              <Route path="/deliveries" element={ <DeliveryPage /> } />
              <Route path="/report" element={ <ReportPage /> } />
              <Route path="/sales" element={ <SalePage /> } />
              <Route path="/stocks" element={ <StocksPage /> } />
              <Route path="/statistics" element={ <StatisticsPage /> } />
              <Route path="/fertilizer-stocks" element={ <FertilizerStockPage /> } />
              <Route path="/spraying-products" element={ <SprayingProductPage /> } />
              <Route path="/spraying-register" element={ <SprayingRegisterPage /> } />
              <Route path="/fertilization-planner" element={ <FertilizationPlannerPage /> } />
              <Route path="/fertilization-comparison" element={ <FertilizationComparisonPage /> } />
              <Route path="/fertilizer-base" element={ <FertilizerBasePage /> } />
              <Route path="/spraying-base" element={ <SprayingBasePage /> } />
              <Route path="/soil-analyses" element={ <SoilAnalysisPage /> } />
              <Route path="/harvest-quality" element={ <HarvestQualityPage /> } />
              <Route path="/nutrient-balance" element={ <NutrientBalancePage /> } />
              <Route path="/season-prices" element={ <SeasonPricePage /> } />
              <Route path="/contractors" element={ <ContractorPage /> } />
              <Route path="/machines" element={ <MachinesPage /> } />
              <Route path="/machines/:id" element={ <MachineDetailPage /> } />
              <Route path="/agopengps" element={ <AgOpenGpsPage /> } />
              <Route path="/parts" element={ <PartsPage /> } />
              <Route path="/passkeys" element={ <PasskeysPage /> } />
            </Routes>
          </Suspense>
        </div>
        <MobileBottomNav />
      </div>
    </SeasonProvider>
  );
}

function App() {
  return (
    <Router>
      <ConfirmProvider>
        <AuthProvider>
          <Routes>
            {/* Public route */}
            <Route path="/login" element={ <LoginPage /> } />

            {/* Protected routes with sidebar layout */}
            <Route path="/*" element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            } />
          </Routes>
        </AuthProvider>
      </ConfirmProvider>
    </Router>
  );
}

export default App;
